/*!
 * Oneskin Rich Text — vanilla JS controller for the Rich text section
 * (staggered reveal animation + play-button video lightbox).
 *
 * Detached from the frontend/ Vite bundle. Loaded directly via:
 *   <script src="{{ 'rich-text.js' | asset_url }}" defer="defer"></script>
 *
 * Replaces:
 *   frontend/scripts/sections/rich-text.js
 *   frontend/scripts/lib/animation/rich-text.js
 */
;(() => {
  'use strict'

  if (window.__oneskinRichText) return
  window.__oneskinRichText = true

  const SECTION_TYPE = 'rich-text'
  const instances = new WeakMap()

  let youtubeApiPromise = null
  let vimeoApiPromise = null

  function shouldAnimate(node) {
    return (
      node.classList.contains('animation') &&
      !document.documentElement.classList.contains('prefers-reduced-motion')
    )
  }

  function delayOffset(node, selector, offsetStart = 0) {
    let offset = offsetStart
    node.querySelectorAll(selector).forEach((item) => {
      item.style.setProperty('--delay-offset-multiplier', String(offset))
      offset++
    })
  }

  function intersectionWatcher(node) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add('is-visible')
          observer.disconnect()
        }
      },
      { threshold: 0.15 }
    )
    observer.observe(node)

    return {
      destroy() {
        observer.disconnect()
      },
    }
  }

  function loadYouTubeAPI() {
    if (window.YT && window.YT.loaded) return Promise.resolve()
    if (youtubeApiPromise) return youtubeApiPromise

    youtubeApiPromise = new Promise((resolve) => {
      const previous = window.onYouTubeIframeAPIReady
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === 'function') previous()
        resolve()
      }
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      document.body.appendChild(tag)
    })

    return youtubeApiPromise
  }

  function loadVimeoAPI() {
    if (window.Vimeo) return Promise.resolve()
    if (vimeoApiPromise) return vimeoApiPromise

    vimeoApiPromise = new Promise((resolve) => {
      const tag = document.createElement('script')
      tag.src = 'https://player.vimeo.com/api/player.js'
      tag.onload = resolve
      document.body.appendChild(tag)
    })

    return vimeoApiPromise
  }

  function ensureLightbox() {
    let dialog = document.getElementById('oneskin-rich-text-video-lightbox')
    if (dialog) return dialog

    dialog = document.createElement('dialog')
    dialog.id = 'oneskin-rich-text-video-lightbox'
    dialog.className = 'rich-text-video-lightbox'
    dialog.innerHTML = `
      <div class="rich-text-video-lightbox__panel">
        <button type="button" class="rich-text-video-lightbox__close" aria-label="Close video">&times;</button>
        <div class="rich-text-video-lightbox__body" data-rich-text-video-body></div>
      </div>
    `
    document.body.appendChild(dialog)

    if (!document.getElementById('oneskin-rich-text-video-lightbox-styles')) {
      const style = document.createElement('style')
      style.id = 'oneskin-rich-text-video-lightbox-styles'
      style.textContent = `
        .rich-text-video-lightbox {
          padding: 0;
          border: 0;
          background: transparent;
          max-width: min(960px, calc(100vw - 2rem));
          width: 100%;
        }
        .rich-text-video-lightbox::backdrop {
          background: rgb(0 0 0 / 0.75);
        }
        .rich-text-video-lightbox__panel {
          position: relative;
          background: #000;
        }
        .rich-text-video-lightbox__close {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          z-index: 2;
          display: grid;
          place-items: center;
          width: 2.25rem;
          height: 2.25rem;
          border: 0;
          border-radius: 9999px;
          background: rgb(0 0 0 / 0.55);
          color: #fff;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
        }
        .rich-text-video-lightbox__body,
        .rich-text-video-lightbox__body video,
        .rich-text-video-lightbox__body iframe,
        .rich-text-video-lightbox__body .play-button-block-video {
          display: block;
          width: 100%;
          aspect-ratio: 16 / 9;
          background: #000;
        }
      `
      document.head.appendChild(style)
    }

    const close = () => {
      const body = dialog.querySelector('[data-rich-text-video-body]')
      if (body) body.innerHTML = ''
      if (dialog.open) dialog.close()
    }

    dialog.querySelector('.rich-text-video-lightbox__close')?.addEventListener('click', close)
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close()
    })
    dialog.addEventListener('close', () => {
      const body = dialog.querySelector('[data-rich-text-video-body]')
      if (body) body.innerHTML = ''
    })

    return dialog
  }

  function initExternalVideo(video) {
    const { videoProvider, videoId } = video.dataset
    if (!videoId) return

    if (videoProvider === 'youtube') {
      loadYouTubeAPI().then(() => {
        const player = new window.YT.Player(video, {
          videoId,
          playerVars: {
            autohide: 0,
            cc_load_policy: 0,
            controls: 1,
            iv_load_policy: 3,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            playlist: videoId,
          },
          events: {
            onReady: () => {
              player.playVideo()
              const iframe = player.getIframe()
              if (iframe) iframe.tabIndex = 0
            },
          },
        })
      })
      return
    }

    if (videoProvider === 'vimeo') {
      loadVimeoAPI().then(() => {
        const player = new window.Vimeo.Player(video, {
          id: videoId,
          controls: true,
          keyboard: false,
        })
        player.play()
        if (player.element) player.element.tabIndex = 0
      })
    }
  }

  function openPlayButton(block) {
    const source = block.querySelector('[data-play-button-block-video-container]')
    if (!source) return

    const dialog = ensureLightbox()
    const body = dialog.querySelector('[data-rich-text-video-body]')
    if (!body) return

    body.innerHTML = source.outerHTML
    const clone = body.querySelector('[data-play-button-block-video-container]')
    const videoType = clone?.dataset.videoType
    const video = body.querySelector('.play-button-block-video, [data-play-button-block-video]')

    dialog.showModal()

    if (!video) return

    if (videoType === 'shopify' && typeof video.play === 'function') {
      video.play().catch(() => {})
      return
    }

    initExternalVideo(video)
  }

  function initPlayButtons(container) {
    const buttons = container.querySelectorAll('[data-play-button-block]')
    const cleanups = []

    buttons.forEach((button) => {
      const block = button.closest('.play-button-block')
      if (!block) return

      const onClick = (event) => {
        event.preventDefault()
        if (button.hasAttribute('data-flash-trigger')) return
        openPlayButton(block)
      }

      button.addEventListener('click', onClick)
      cleanups.push(() => button.removeEventListener('click', onClick))
    })

    return {
      unload() {
        cleanups.forEach((fn) => fn())
      },
    }
  }

  function initRichText(container) {
    const playButtons = initPlayButtons(container)
    let revealAnimation = null

    if (shouldAnimate(container)) {
      delayOffset(container, '[data-rich-text-blocks] > *')
      revealAnimation = intersectionWatcher(container)
    }

    return {
      unload() {
        playButtons.unload()
        revealAnimation?.destroy()
      },
    }
  }

  function initContainer(container) {
    if (!container || instances.has(container)) return
    if (container.dataset.sectionType !== SECTION_TYPE) return
    instances.set(container, initRichText(container))
  }

  function unloadContainer(container) {
    instances.get(container)?.unload?.()
    instances.delete(container)
  }

  function autoInit(root = document) {
    root.querySelectorAll(`[data-section-type="${SECTION_TYPE}"]`).forEach(initContainer)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoInit())
  } else {
    autoInit()
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionId = event.detail?.sectionId
    const container = sectionId
      ? document.querySelector(`[data-section-id="${sectionId}"]`)
      : null
    if (container) initContainer(container)
    else autoInit(event.target)
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId
    const container = sectionId
      ? document.querySelector(`[data-section-id="${sectionId}"]`)
      : null
    if (container) unloadContainer(container)
  })
})()
